import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API, apiError } from "@/lib/api";
import { offerKeyFromUrl } from "@/config/branding";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/Markdown";
import { MiraAvatar } from "@/components/MiraAvatar";
import { toast } from "sonner";
import { ArrowUp, Loader2, Plus, MessageSquare, X, History } from "lucide-react";

const QUICK = [
  "How do I get more students without spending more on ads?",
  "People reach out but never come in — why?",
  "My follow-up is a mess. Where do I start?",
  "I keep losing members. What do I do?",
  "My Facebook ads aren't working. What's wrong?",
  "How do I finally fill my classes?",
];

export default function Coach() {
  const [params, setParams] = useSearchParams();
  const [convId, setConvId] = useState(null);
  const [thread, setThread] = useState([]);
  const [input, setInput] = useState("");
  const [persona, setPersona] = useState("growth");
  const [streaming, setStreaming] = useState(false);
  const [convos, setConvos] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const endRef = useRef(null);
  const started = useRef(false);

  const loadConvos = useCallback(async () => {
    try { const { data } = await api.get("/conversations"); setConvos(data); } catch {}
  }, []);

  useEffect(() => { loadConvos(); }, [loadConvos]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread, streaming]);

  const openConversation = async (id) => {
    setConvId(id); setDrawerOpen(false);
    try {
      const { data } = await api.get(`/conversations/${id}/messages`);
      setThread(data.map((m) => ({ role: m.role, content: m.content })));
    } catch { setThread([]); }
  };

  const newChat = () => { setConvId(null); setThread([]); setDrawerOpen(false); };

  const trackOffer = async (url) => {
    try { await api.post("/cta/click", { cta_type: offerKeyFromUrl(url), context: convId }); } catch {}
  };

  const send = useCallback(async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setThread((t) => [...t, { role: "user", content: message }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/coach/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, conversation_id: convId, persona }),
      });
      if (!res.ok || !res.body) throw new Error("Coach unavailable");
      const newCid = res.headers.get("X-Conversation-Id");
      if (newCid && !convId) setConvId(newCid);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setThread((t) => {
          const copy = [...t];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
      loadConvos();
    } catch (e) {
      toast.error(apiError(e.message));
      setThread((t) => { const c = [...t]; c[c.length - 1] = { role: "assistant", content: "Sorry — I hit an error. Please try again." }; return c; });
    } finally {
      setStreaming(false);
    }
  }, [input, streaming, convId, persona, loadConvos]);

  useEffect(() => {
    if (started.current) return;
    const c = params.get("c");
    const q = params.get("q");
    const p = params.get("persona");
    if (p) setPersona(p);
    if (c) { started.current = true; setParams({}, { replace: true }); openConversation(c); return; }
    if (q) { started.current = true; setParams({}, { replace: true }); setTimeout(() => send(q), 100); }
    // eslint-disable-next-line
  }, []);

  const empty = thread.length === 0;

  const ConvoList = ({ inDrawer }) => (
    <>
      <Button data-testid={inDrawer ? "drawer-new-chat-btn" : "new-chat-btn"} onClick={newChat} className="mb-3 w-full bg-slate-800 hover:bg-slate-700 text-slate-100 justify-start">
        <Plus className="h-4 w-4 mr-2" /> New chat
      </Button>
      <div className={`space-y-1 ${inDrawer ? "" : "flex-1 overflow-y-auto mm-scroll"}`}>
        {convos.length === 0 && <p className="text-xs text-slate-500 px-3 py-2">No past chats yet.</p>}
        {convos.map((c) => (
          <button
            key={c.id} onClick={() => openConversation(c.id)} data-testid="conversation-item"
            className={`w-full text-left px-3 py-3 rounded-xl text-sm truncate flex items-center gap-2 ${
              convId === c.id ? "bg-emerald-500/15 text-emerald-200" : "text-slate-400 hover:bg-slate-800/60"
            }`}
          >
            <MessageSquare className="h-4 w-4 shrink-0" /> {c.title || "New chat"}
          </button>
        ))}
      </div>
    </>
  );

  return (
    <div className="flex gap-6 h-full">
      {/* Desktop conversation list */}
      <aside className="hidden xl:flex flex-col w-60 shrink-0">
        <ConvoList inDrawer={false} />
      </aside>

      {/* Chat area */}
      <div className="flex-1 flex flex-col min-w-0 h-full">
        {/* Mobile toolbar */}
        <div className="flex xl:hidden items-center justify-between mb-2 shrink-0">
          <Button variant="outline" size="sm" data-testid="history-btn" onClick={() => setDrawerOpen(true)}
            className="border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-200 h-9">
            <History className="h-4 w-4 mr-2" /> Past chats
          </Button>
          <Button variant="ghost" size="sm" data-testid="mobile-new-chat-btn" onClick={newChat} className="text-emerald-300 h-9">
            <Plus className="h-4 w-4 mr-1.5" /> New
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto mm-scroll">
          {empty ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <MiraAvatar className="h-16 w-16 mb-4" />
              <h2 className="font-display text-2xl sm:text-3xl font-bold">Chat with Mira</h2>
              <p className="mt-2 text-[15px] text-slate-400 max-w-md">Tell me what's on your mind, or tap one of these and I'll help you build your plan.</p>
              <div className="mt-6 flex flex-wrap gap-2 justify-center max-w-2xl">
                {QUICK.map((q) => (
                  <button key={q} onClick={() => send(q)} data-testid="quick-action"
                    className="text-[15px] text-slate-200 bg-slate-900 border border-slate-800 hover:border-emerald-500/40 hover:text-emerald-300 rounded-full px-4 py-2.5 transition-colors">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-1">
              {thread.map((m, i) => (
                <div key={i} className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" && <MiraAvatar className="h-8 w-8 mb-1" />}
                  <div className={`max-w-[88%] sm:max-w-[85%] rounded-3xl px-4 py-3 ${
                    m.role === "user" ? "bg-emerald-500 text-slate-950 font-medium rounded-br-lg" : "bg-slate-900 border border-slate-800 text-slate-100 rounded-bl-lg"
                  }`}>
                    {m.content ? (
                      m.role === "assistant"
                        ? <Markdown className="text-base leading-relaxed" onOfferClick={trackOffer}>{m.content}</Markdown>
                        : <div className="whitespace-pre-wrap text-base leading-relaxed">{m.content}</div>
                    ) : (
                      <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />
                    )}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
          )}
        </div>

        {/* Input */}
        <div className="pt-2 shrink-0">
          <div className="flex items-end gap-2 bg-slate-900 border border-slate-700 rounded-3xl p-2 focus-within:border-emerald-500/50 transition-colors">
            <textarea
              data-testid="coach-input" value={input} onChange={(e) => setInput(e.target.value)} rows={1}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Message Mira…"
              className="flex-1 resize-none bg-transparent px-3 py-2.5 text-base outline-none placeholder:text-slate-500 max-h-32"
            />
            <Button data-testid="coach-send" onClick={() => send()} disabled={streaming || !input.trim()}
              className="h-11 w-11 p-0 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 shrink-0">
              {streaming ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowUp className="h-5 w-5" strokeWidth={2.5} />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile history drawer */}
      {drawerOpen && (
        <div className="xl:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDrawerOpen(false)} />
          <div className="relative w-72 max-w-[80%] bg-slate-950 border-r border-slate-800 p-4 mm-fade-in flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <span className="font-display font-semibold text-slate-100">Your chats</span>
              <button onClick={() => setDrawerOpen(false)} className="p-2 text-slate-300"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto mm-scroll">
              <ConvoList inDrawer />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
